/**
 * The contract between the server render and the hydrating client.
 *
 * Kept deliberately small: anything in here is emitted into the HTML of a page that may be
 * cached and served to every visitor, so it must be true for an anonymous visitor and nobody
 * else. See `ssrViewer.ts` for why that is the whole safety story.
 */

/** Keys under `SsrPayload.data`, so the loader and the consuming page cannot drift. */
export const ssrDataKeys = {
    article: (slug: string) => `article:${slug}`,
    cmsPage: (slug: string) => `cms-page:${slug}`,
    articleList: () => 'article-list',
    changelogList: () => 'changelog-list',
    changelog: (slug: string) => `changelog:${slug}`,
} as const;

export interface SsrPayload {
    /** Route pattern that was rendered, for logs and for the `x-kuder-ssr` response header. */
    route: string;
    /**
     * The feature flags the server rendered with. Components branch on them outside any SSR region,
     * so the client's first render needs the same values; the client seeds them into its RTK Query
     * cache before hydrating (App.tsx). Only the flags, not the RTK state around them.
     */
    flags?: Record<string, boolean>;
    /** Imperative loads keyed by `ssrDataKeys`. */
    data: Record<string, unknown>;
}

declare global {
    interface Window {
        __GOZIG_SSR__?: SsrPayload;
    }
}
